import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {shareReplay, tap} from 'rxjs/operators';

import {AgentDetailsResponse, ClientDetailsResponse} from '../model/agentDetailsResponse.model';
import {MetaFileLink} from 'src/app/models/metaFileLink.model';
import {UserService} from 'src/app/services/shared/user.service';

import {SnackbarService} from 'src/app/services/snackbar.service';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {ErrorMatcher, ErrorType} from 'src/app/shared/errors/ErrorMatcher';
import {OrganizationDetailsReqRes} from '../model/OrganizationDetailsReqRes.model';
import FeatureFlags from '../model/FeatureFlags.enum';
import {AddressFormSettings} from 'src/app/shared/components/address-form/address-form.settings';
import RoleFlags from '../model/RoleFlags.enum';
import {AppSettingsService} from 'src/app/services/app-settings.service';

@Component({
  selector: 'terra-company-profile',
  templateUrl: './company-profile.component.html',
  styleUrls: ['./company-profile.component.scss']
})
export class CompanyProfileComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  private subs = {
    logoChangeSub: null as Subscription
  };
  agentDetails: AgentDetailsResponse;
  FeatureFlags = FeatureFlags;
  RoleFlags = RoleFlags;
  organizationMembers$ = this.userService.getCurrentAgentOrganizationMembers().pipe(tap(members => this.organizationMembers = members), shareReplay(1));
  organizationMembers: ClientDetailsResponse[];
  isWhitelabelingEnabled$ = this.userService.userHasFeatureFlag(FeatureFlags.EmailWhitelabeling);

  pageForm: UntypedFormGroup;

  isFileNotSupported = false;
  fileNotSupportedError = 'The file you uploaded is not supported.';

  logoExtensionTypes = TerraUtils.consts.supportedFileExtensions.LOGO;

  isSubmitted = false;
  isGeneralServerError = false;
  generalServerErrorMessage: string = TerraUtils.consts.messages.GENERAL_SUBMIT_ERROR;

  addressFromSettings = new AddressFormSettings();

  @Input()
  set model(model: AgentDetailsResponse) {
    if (model && !this.agentDetails) {
      this.agentDetails = model;
    }
  }

  get model() {
    return this.agentDetails;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private appSettingService: AppSettingsService,
    private snackbarService: SnackbarService
  ) {
    super();
  }

  ngOnInit() {
    this.generatePageForm();

    this.subs.logoChangeSub = this.pageForm.get('logoMetaFileLink').valueChanges.subscribe((metaFileLink: MetaFileLink) => {
      this.isFileNotSupported = false;
      this.agentDetails.organizationDetails.logoMetaFileLink = metaFileLink;
    });
  }

  generatePageForm() {
    this.pageForm = this.fb.group({
      name: [this.agentDetails.organizationDetails.name, Validators.required],
      logoMetaFileLink: [this.agentDetails.organizationDetails.logoMetaFileLink],
      locationDetails: {},
      showGpPromoteToLPs: [this.agentDetails.organizationDetails.showGpPromoteToLPs],
      publicOpportunitiesPage: [{
        value: FeatureFlags.isOn(this.model.featureFlags, FeatureFlags.InvestNow) ? this.agentDetails.organizationDetails.publicOpportunitiesPage : false,
        disabled: !FeatureFlags.isOn(this.model.featureFlags, FeatureFlags.InvestNow)
      }]
    });
    this.pageForm.get('locationDetails').patchValue(this.agentDetails.organizationDetails.locationDetails);
    this.pageForm.valueChanges.subscribe(values => {
      let f = (values.featureFlagsAi && this.FeatureFlags.AiRanking) | (values.featureFlagsDis && this.FeatureFlags.DistributionsGP);
    });
  }

  save() {
    this.isSubmitted = true;
    this.isGeneralServerError = false;
    this.isFileNotSupported = false;
    this.pageForm.markAllAsTouched();
    this.pageForm.updateValueAndValidity();
    if (this.pageForm.valid) {
      const model = this.pageForm.value as OrganizationDetailsReqRes;

      this.userService.companyProfileGpUpdate(model).pipe(
        untilComponentDestroyed(this)
      ).subscribe(
        (profileUpdateRes) => {
          if (profileUpdateRes) {
            this.snackbarService.showGeneralMessage(`Saved`);
          } else {
            this.snackbarService.showGeneralMessage(`Couldn't Save`);
          }
        },
        error => {
          if (ErrorMatcher.isError(error, ErrorType.BadFileFormatException)) {
            this.isFileNotSupported = true;
          } else {
            this.isGeneralServerError = true;
          }
          console.log('Error', error);
        }
      );
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.subs.logoChangeSub
    ) {
      this.subs.logoChangeSub.unsubscribe();
    }
  }

  get brandedInvestorLink() {
    const gpref = `?gpref=${this.agentDetails?.organizationDetails?.referralKey}`;
    return `${this.appSettingService.lpFrontEnvironmentAppBaseUrl}lp/login${!!this.agentDetails?.organizationDetails?.referralKey ? gpref : ''}`;
  }

  get publicOpportunitiesPageLink() {
    return (this.agentDetails.organizationDetails.publicOpportunitiesPageLink 
      || `${this.appSettingService.lpFrontEnvironmentAppBaseUrl}lp/opportunity/${this.agentDetails?.organizationDetails?.referralKey}/campaign`) + '?isRegistered=False';
  }

  snackbarMessage(itemName: string, e: Event) {
    if (!!itemName) {
      this.snackbarService.showGeneralMessage(`${itemName} was copied to the clipboard`, 1);
    }
    e.preventDefault();
  }
}
